import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContext';

export const ProjectSettings = ({ open, onClose, project, onUpdate }) => {
  const { language } = useLanguage();
  const [settings, setSettings] = useState({
    title: project?.title || '',
    description: project?.description || '',
    style: project?.style || 'cinematic',
    pacing: project?.pacing || 'balanced',
    aspect_ratio: project?.aspect_ratio || '16:9',
    time_period: project?.time_period || 'auto',
    include_text_suggestions: project?.include_text_suggestions || false
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(settings);
    setSaving(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{language === 'es' ? 'Configuración del Proyecto' : 'Project Settings'}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label className="text-[#F8FAFC]">{language === 'es' ? 'Título del Proyecto' : 'Project Title'}</Label>
            <Input
              value={settings.title}
              onChange={(e) => setSettings({ ...settings, title: e.target.value })}
              className="bg-[#0a0a0a] border-[#333333] text-white"
              data-testid="settings-title-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#F8FAFC]">{language === 'es' ? 'Descripción' : 'Description'}</Label>
            <Textarea
              value={settings.description}
              onChange={(e) => setSettings({ ...settings, description: e.target.value })}
              className="bg-[#0a0a0a] border-[#333333] text-white resize-none"
              rows={3}
              data-testid="settings-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#F8FAFC]">{language === 'es' ? 'Estilo Visual' : 'Visual Style'}</Label>
              <Select value={settings.style} onValueChange={(v) => setSettings({ ...settings, style: v })}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white" data-testid="settings-style-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#333333]">
                  <SelectItem value="cinematic">{language === 'es' ? 'Cinematográfico' : 'Cinematic'}</SelectItem>
                  <SelectItem value="documentary">{language === 'es' ? 'Documental' : 'Documentary'}</SelectItem>
                  <SelectItem value="realistic">{language === 'es' ? 'Realista' : 'Realistic'}</SelectItem>
                  <SelectItem value="vintage">Vintage</SelectItem>
                  <SelectItem value="dramatic">{language === 'es' ? 'Dramático' : 'Dramatic'}</SelectItem>
                  <SelectItem value="modern">{language === 'es' ? 'YouTube Moderno' : 'Modern YouTube'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#F8FAFC]">{language === 'es' ? 'Ritmo' : 'Pacing'}</Label>
              <Select value={settings.pacing} onValueChange={(v) => setSettings({ ...settings, pacing: v })}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white" data-testid="settings-pacing-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#333333]">
                  <SelectItem value="slower">{language === 'es' ? 'Cinematográfico Lento' : 'Slower Cinematic'}</SelectItem>
                  <SelectItem value="balanced">{language === 'es' ? 'Documental Equilibrado' : 'Balanced Documentary'}</SelectItem>
                  <SelectItem value="faster">{language === 'es' ? 'YouTube Rápido' : 'Faster YouTube'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Time Period - NEW */}
          <div className="space-y-2">
            <Label className="text-[#F8FAFC]">{language === 'es' ? 'Período de Tiempo / Era' : 'Time Period / Era'}</Label>
            <p className="text-xs text-[#A1A1AA] mb-1">{language === 'es' ? 'Configura el período histórico para las imágenes generadas' : 'Set the historical period for generated images'}</p>
            <Select value={settings.time_period} onValueChange={(v) => setSettings({ ...settings, time_period: v })}>
              <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white" data-testid="settings-time-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#333333]">
                <SelectItem value="auto">{language === 'es' ? 'Auto (la IA decide)' : 'Auto (AI decides)'}</SelectItem>
                <SelectItem value="biblical">{language === 'es' ? 'Bíblico / Antiguo Medio Oriente' : 'Biblical / Ancient Middle East'}</SelectItem>
                <SelectItem value="ancient">{language === 'es' ? 'Antiguo (antes del 500 DC)' : 'Ancient (before 500 AD)'}</SelectItem>
                <SelectItem value="medieval">{language === 'es' ? 'Medieval (500-1500 DC)' : 'Medieval (500-1500 AD)'}</SelectItem>
                <SelectItem value="renaissance">{language === 'es' ? 'Renacimiento (1400-1600)' : 'Renaissance (1400-1600)'}</SelectItem>
                <SelectItem value="victorian">{language === 'es' ? 'Victoriano (1837-1901)' : 'Victorian (1837-1901)'}</SelectItem>
                <SelectItem value="modern">{language === 'es' ? 'Moderno (Día Presente)' : 'Modern (Present Day)'}</SelectItem>
                <SelectItem value="futuristic">{language === 'es' ? 'Futurista' : 'Futuristic'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#F8FAFC]">{language === 'es' ? 'Relación de Aspecto' : 'Aspect Ratio'}</Label>
              <Select value={settings.aspect_ratio} onValueChange={(v) => setSettings({ ...settings, aspect_ratio: v })}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white" data-testid="settings-ratio-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#333333]">
                  <SelectItem value="16:9">{language === 'es' ? '16:9 Horizontal' : '16:9 Landscape'}</SelectItem>
                  <SelectItem value="9:16">{language === 'es' ? '9:16 Vertical' : '9:16 Vertical'}</SelectItem>
                  <SelectItem value="1:1">{language === 'es' ? '1:1 Cuadrado' : '1:1 Square'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-[#F8FAFC]">{language === 'es' ? 'Sugerencias de Texto en Pantalla' : 'On-Screen Text Suggestions'}</Label>
              <p className="text-xs text-[#A1A1AA]">{language === 'es' ? 'Incluir sugerencias de texto superpuesto en los prompts' : 'Include text overlay suggestions in prompts'}</p>
            </div>
            <Switch
              checked={settings.include_text_suggestions}
              onCheckedChange={(v) => setSettings({ ...settings, include_text_suggestions: v })}
              data-testid="settings-text-suggestions-switch"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#A1A1AA] hover:text-white">
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#FF5D01] hover:bg-[#e65400] text-white"
            data-testid="save-settings-btn"
          >
            {saving ? (language === 'es' ? 'Guardando...' : 'Saving...') : (language === 'es' ? 'Guardar Configuración' : 'Save Settings')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
